// Small SVG charts: a single-series trend (progress) and the attempt evidence
// plot (body rise + foot positions over time, as small multiples sharing the
// time axis — never a dual axis). Both have a crosshair + tooltip.

import { useMemo, useRef, useState, type PointerEvent as RPointerEvent } from 'react';
import type { AttemptAnalysis } from '../../core/analysis';
import type { Reference } from '../../core/reference';

function niceTicks(min: number, max: number, count = 4): number[] {
  if (!(max > min)) return [min];
  const raw = (max - min) / count;
  const mag = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * mag).find((s) => s >= raw) ?? raw;
  const out: number[] = [];
  for (let v = Math.ceil(min / step) * step; v <= max + 1e-9; v += step) out.push(+v.toFixed(10));
  return out;
}

function pathFor(points: ({ x: number; y: number } | null)[]): string {
  let d = '';
  let pen = false;
  for (const p of points) {
    if (!p) {
      pen = false;
      continue;
    }
    d += `${pen ? 'L' : 'M'}${p.x.toFixed(1)},${p.y.toFixed(1)}`;
    pen = true;
  }
  return d;
}

// ---------------------------------------------------------------------------

export interface TrendPoint {
  label: string;
  value: number | null;
}

export function TrendChart({
  data,
  format,
  color = 'var(--accent)',
  domain,
  ariaLabel,
}: {
  data: TrendPoint[];
  format: (v: number) => string;
  color?: string;
  domain?: [number, number];
  ariaLabel: string;
}) {
  const W = 360;
  const H = 130;
  const pad = { l: 38, r: 14, t: 12, b: 22 };
  const vals = data.map((d) => d.value).filter((v): v is number => v !== null);
  const lo = domain?.[0] ?? Math.min(...vals, 0);
  const hi = domain?.[1] ?? Math.max(...vals, 1) * 1.1;
  const ticks = niceTicks(lo, hi, 3);
  const x = (i: number) => pad.l + (data.length <= 1 ? (W - pad.l - pad.r) / 2 : (i / (data.length - 1)) * (W - pad.l - pad.r));
  const y = (v: number) => pad.t + (1 - (v - lo) / (hi - lo || 1)) * (H - pad.t - pad.b);
  const pts = data.map((d, i) => (d.value === null ? null : { x: x(i), y: y(d.value) }));
  const [hover, setHover] = useState<number | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const onMove = (e: RPointerEvent) => {
    const b = svgRef.current!.getBoundingClientRect();
    const px = ((e.clientX - b.left) / b.width) * W;
    let best = 0;
    data.forEach((_, i) => {
      if (Math.abs(x(i) - px) < Math.abs(x(best) - px)) best = i;
    });
    setHover(best);
  };
  const last = [...pts].reverse().find(Boolean);
  if (!vals.length) return <p className="faint small">Not enough data yet.</p>;
  return (
    <div className="chart" onPointerLeave={() => setHover(null)}>
      <svg ref={svgRef} viewBox={`0 0 ${W} ${H}`} role="img" aria-label={ariaLabel} onPointerMove={onMove} onPointerDown={onMove}>
        <g className="grid">
          {ticks.map((t) => (
            <line key={t} x1={pad.l} x2={W - pad.r} y1={y(t)} y2={y(t)} />
          ))}
        </g>
        <g className="axis">
          {ticks.map((t) => (
            <text key={t} x={pad.l - 6} y={y(t) + 4} textAnchor="end">
              {format(t)}
            </text>
          ))}
          {data.length > 1 && (
            <>
              <text x={x(0)} y={H - 6} textAnchor="start">{data[0].label}</text>
              <text x={x(data.length - 1)} y={H - 6} textAnchor="end">{data[data.length - 1].label}</text>
            </>
          )}
        </g>
        <path d={pathFor(pts)} fill="none" stroke={color} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
        {pts.map((p, i) => p && data.length <= 24 && <circle key={i} cx={p.x} cy={p.y} r={2.5} fill={color} />)}
        {last && <circle cx={last.x} cy={last.y} r={4.5} fill={color} stroke="var(--surface)" strokeWidth={2} />}
        {hover !== null && (
          <line x1={x(hover)} x2={x(hover)} y1={pad.t} y2={H - pad.b} stroke="var(--text-3)" strokeWidth={1} />
        )}
        {hover !== null && pts[hover] && <circle cx={pts[hover]!.x} cy={pts[hover]!.y} r={5} fill={color} stroke="var(--surface)" strokeWidth={2} />}
      </svg>
      {hover !== null && (
        <div className="tooltip" style={{ left: `${(x(hover) / W) * 100}%`, top: `${((pts[hover]?.y ?? pad.t) / H) * 100}%` }}>
          <b>{data[hover].value === null ? '—' : format(data[hover].value!)}</b> <span className="faint">{data[hover].label}</span>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

export function EvidencePlot({ analysis, reference, cueTime }: { analysis: AttemptAnalysis; reference: Reference; cueTime: number | null }) {
  const s = analysis.series;
  const e = analysis.events;
  const W = 360;
  const pad = { l: 40, r: 12 };
  const origin = cueTime ?? s.t[0] ?? 0;
  const t0 = (s.t[0] ?? 0) - origin;
  const t1 = (s.t[s.t.length - 1] ?? 1) - origin;
  const x = (t: number) => pad.l + ((t - origin - t0) / (t1 - t0 || 1)) * (W - pad.l - pad.r);
  const [hover, setHover] = useState<number | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  const ys = useMemo(() => {
    const vals = [...s.frontY, ...s.rearY].filter((v): v is number => v !== null);
    const lo = Math.min(reference.rear.centre.y - 25, ...vals.filter((v) => v > -150));
    const hi = Math.max(reference.front.centre.y + 25, ...vals.filter((v) => v < 250));
    return { lo, hi };
  }, [s, reference]);

  const P1 = { top: 16, h: 64 };
  const P2 = { top: 112, h: 110 };
  const H = P2.top + P2.h + 26;
  const yRise = (v: number) => P1.top + (1 - Math.max(-0.2, Math.min(1.2, v)) / 1.2) * P1.h;
  const yFoot = (v: number) => P2.top + (1 - (v - ys.lo) / (ys.hi - ys.lo)) * P2.h;
  const risePts = s.t.map((t, i) => (s.rise[i] === null ? null : { x: x(t), y: yRise(s.rise[i]!) }));
  const frontPts = s.t.map((t, i) => (s.frontY[i] === null ? null : { x: x(t), y: yFoot(s.frontY[i]!) }));
  const rearPts = s.t.map((t, i) => (s.rearY[i] === null ? null : { x: x(t), y: yFoot(s.rearY[i]!) }));
  const timeTicks = niceTicks(t0, t1, 5);

  const onMove = (ev: RPointerEvent) => {
    const b = svgRef.current!.getBoundingClientRect();
    const px = ((ev.clientX - b.left) / b.width) * W;
    let best = 0;
    s.t.forEach((t, i) => {
      if (Math.abs(x(t) - px) < Math.abs(x(s.t[best]) - px)) best = i;
    });
    setHover(best);
  };

  const band = (a: number | undefined, b: number | undefined, fill: string, key: string) =>
    a !== undefined && b !== undefined ? (
      <rect key={key} x={x(a)} width={Math.max(1, x(b) - x(a))} y={P1.top - 4} height={P2.top + P2.h - P1.top + 4} fill={fill} />
    ) : null;
  const marker = (t: number | undefined, label: string, key: string) =>
    t === undefined ? null : (
      <g key={key}>
        <line x1={x(t)} x2={x(t)} y1={P1.top - 6} y2={P2.top + P2.h} stroke="var(--text-3)" strokeWidth={1} />
        <text x={x(t)} y={P1.top - 8} textAnchor="middle" fontSize={10} fill="var(--text-2)">
          {label}
        </text>
      </g>
    );

  return (
    <div className="chart" onPointerLeave={() => setHover(null)}>
      <svg ref={svgRef} viewBox={`0 0 ${W} ${H}`} role="img" aria-label="How the body and feet moved during the attempt" onPointerMove={onMove} onPointerDown={onMove}>
        {band(analysis.initial?.t0, analysis.initial?.t1, 'var(--accent-soft)', 'b-land')}
        {band(e.holdStart, e.holdEnd, 'var(--good-soft)', 'b-hold')}
        <text x={pad.l} y={P1.top + 2} fontSize={11} fill="var(--text-2)" fontWeight={600}>
          Body rise
        </text>
        <g className="grid">
          <line x1={pad.l} x2={W - pad.r} y1={yRise(0)} y2={yRise(0)} />
          <line x1={pad.l} x2={W - pad.r} y1={yRise(1)} y2={yRise(1)} />
        </g>
        <g className="axis">
          <text x={pad.l - 6} y={yRise(0) + 4} textAnchor="end">lying</text>
          <text x={pad.l - 6} y={yRise(1) + 4} textAnchor="end">up</text>
        </g>
        <path d={pathFor(risePts)} fill="none" stroke="var(--text-2)" strokeWidth={2} strokeLinejoin="round" />

        <text x={pad.l} y={P2.top - 8} fontSize={11} fill="var(--text-2)" fontWeight={600}>
          Feet along the board (cm from back target)
        </text>
        <g className="grid">
          {niceTicks(ys.lo, ys.hi, 4).map((v) => (
            <line key={v} x1={pad.l} x2={W - pad.r} y1={yFoot(v)} y2={yFoot(v)} />
          ))}
        </g>
        <g className="axis">
          {niceTicks(ys.lo, ys.hi, 4).map((v) => (
            <text key={v} x={pad.l - 6} y={yFoot(v) + 4} textAnchor="end">
              {Math.round(v - reference.rear.centre.y)}
            </text>
          ))}
          {timeTicks.map((t) => (
            <text key={t} x={x(t + origin)} y={H - 8} textAnchor="middle">
              {(t / 1000).toFixed(1)}s
            </text>
          ))}
        </g>
        <line x1={pad.l} x2={W - pad.r} y1={yFoot(reference.front.centre.y)} y2={yFoot(reference.front.centre.y)} stroke="var(--front)" strokeOpacity={0.45} strokeWidth={1} />
        <line x1={pad.l} x2={W - pad.r} y1={yFoot(reference.rear.centre.y)} y2={yFoot(reference.rear.centre.y)} stroke="var(--rear)" strokeOpacity={0.45} strokeWidth={1} />
        <path d={pathFor(rearPts)} fill="none" stroke="var(--rear)" strokeWidth={2} strokeLinejoin="round" />
        <path d={pathFor(frontPts)} fill="none" stroke="var(--front)" strokeWidth={2} strokeLinejoin="round" />

        {marker(e.cue, 'go', 'm-cue')}
        {marker(e.onset, 'move', 'm-onset')}
        {marker(e.landing, 'land', 'm-land')}

        {hover !== null && <line x1={x(s.t[hover])} x2={x(s.t[hover])} y1={P1.top} y2={P2.top + P2.h} stroke="var(--text)" strokeOpacity={0.5} strokeWidth={1} />}
      </svg>
      {hover !== null && (
        <div className="tooltip" style={{ left: `${(x(s.t[hover]) / W) * 100}%`, top: `${(P2.top / H) * 100}%` }}>
          <div>
            <b>{((s.t[hover] - origin) / 1000).toFixed(2)} s</b>
          </div>
          <div>
            <span className="key" style={{ background: 'var(--text-2)' }} />
            rise <b>{s.rise[hover] === null ? '—' : s.rise[hover]!.toFixed(2)}</b>
          </div>
          <div>
            <span className="key" style={{ background: 'var(--front)' }} />
            front <b>{s.frontY[hover] === null ? 'not seen' : `${Math.round(s.frontY[hover]! - reference.rear.centre.y)} cm`}</b>
          </div>
          <div>
            <span className="key" style={{ background: 'var(--rear)' }} />
            back <b>{s.rearY[hover] === null ? 'not seen' : `${Math.round(s.rearY[hover]! - reference.rear.centre.y)} cm`}</b>
          </div>
        </div>
      )}
      <div className="legend" style={{ marginTop: 6 }}>
        <span>
          <i className="dot front" /> Front foot
        </span>
        <span>
          <i className="dot rear" /> Back foot
        </span>
        <span>
          <i className="dot" style={{ background: 'var(--accent-soft)', boxShadow: 'none', borderRadius: 3 }} /> landing
        </span>
        <span>
          <i className="dot" style={{ background: 'var(--good-soft)', boxShadow: 'none', borderRadius: 3 }} /> hold
        </span>
      </div>
    </div>
  );
}
